import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useContadorScope } from '../hooks/useContadorScope';
import ContadorClientPicker from '../components/ContadorClientPicker';
import { formatMoney } from '../utils/money';
import {
  agingLabel,
  agingTone,
  daysOverdue,
  isPendiente,
  saldoPendiente,
} from '../utils/cobranzas';
import { canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { isReadOnlyUser } from '../utils/access';
import { downloadBinaryExport } from '../utils/downloadExport';
import {
  FiDownload,
  FiBarChart2,
  FiCalendar,
  FiTrendingUp,
  FiRefreshCw,
  FiEye,
  FiAlertCircle,
  FiFileText,
  FiLock,
} from 'react-icons/fi';
import ProjectedCashflowPanel from '../components/ProjectedCashflowPanel';

function monthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return { desde: `${y}-${m}-01`, hasta: `${y}-${m}-${String(last).padStart(2, '0')}` };
}

function monthLabel() {
  return new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
}

function formatFecha(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const REGIMEN_LABELS = {
  monotributo: 'Monotributo',
  ri: 'Responsable inscripto',
};

function fiscalUrgency(ev) {
  if (ev.vencido) return { label: 'Vencido', tone: 'red' };
  if (ev.diasRestantes === 0) return { label: 'Hoy', tone: 'amber' };
  if (ev.diasRestantes <= 7) return { label: `${ev.diasRestantes}d`, tone: 'amber' };
  if (ev.diasRestantes <= 30) return { label: `${ev.diasRestantes}d`, tone: 'sky' };
  return { label: formatFecha(ev.fecha), tone: 'muted' };
}

function urgencyClasses(tone, modoOscuro) {
  if (tone === 'red') return modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700';
  if (tone === 'amber') return modoOscuro ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-50 text-amber-800';
  if (tone === 'sky') return modoOscuro ? 'bg-sky-900/40 text-sky-200' : 'bg-sky-50 text-sky-800';
  return modoOscuro ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600';
}

function KpiCard({ label, value, hint, color, modoOscuro, highlight }) {
  return (
    <div className={`px-4 py-3 ${highlight ? (modoOscuro ? 'bg-[#1F80FF]/10' : 'bg-[#1F80FF]/5') : (modoOscuro ? 'bg-gray-900' : 'bg-white')}`}>
      <div className="text-[10px] font-semibold tracking-wide uppercase text-gray-400">{label}</div>
      <div className={`text-base sm:text-lg font-semibold tabular-nums mt-0.5 ${color}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

const MOBILE_SECTIONS = [
  { id: 'flujo', label: 'Flujo' },
  { id: 'proyeccion', label: 'Proyección' },
  { id: 'fiscal', label: 'Fiscal' },
  { id: 'impagas', label: 'Impagas' },
];

function scrollToContadorSection(id) {
  document.getElementById(`contador-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Skeleton({ box, modoOscuro }) {
  const pulse = modoOscuro ? 'bg-gray-800 animate-pulse' : 'bg-gray-200 animate-pulse';
  return (
    <div className={`rounded-2xl border overflow-hidden ${box} p-4 space-y-4`}>
      <div className={`h-4 w-40 rounded ${pulse}`} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-16 rounded-xl ${pulse}`} />
        ))}
      </div>
      <div className={`h-32 rounded-xl ${pulse}`} />
    </div>
  );
}

export default function Contador() {
  const { user, token } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const {
    clients,
    multiClient,
    loadingClients,
    activeCompanyId,
    setActiveCompanyId,
    activeClient,
    scopedFetch,
    isViewerPortal,
  } = useContadorScope();

  const [flujo, setFlujo] = useState(null);
  const [proyeccion, setProyeccion] = useState(null);
  const [calendario, setCalendario] = useState(null);
  const [impagas, setImpagas] = useState([]);
  const [impagasTotal, setImpagasTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteSending, setNoteSending] = useState(false);
  const [noteMessage, setNoteMessage] = useState('');
  const [clientNotes, setClientNotes] = useState([]);

  const hasProfesional = useMemo(() => canAccessProfesionalFeatures(user), [user]);
  const readOnly = useMemo(() => isReadOnlyUser(user), [user]);

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-700';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = modoOscuro ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white';
  const divide = modoOscuro ? 'border-gray-800' : 'border-gray-100';
  const btnOut = modoOscuro
    ? 'border-gray-700 hover:bg-gray-800 text-gray-200'
    : 'border-gray-200 hover:bg-white text-gray-800';

  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (isViewerPortal && !activeCompanyId) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const { desde, hasta } = monthRange();
      const requests = [
        scopedFetch(`/api/reportes/flujo-caja?desde=${desde}&hasta=${hasta}`),
        scopedFetch('/api/fiscal/calendario'),
        scopedFetch('/api/invoices'),
      ];
      if (hasProfesional) {
        requests.splice(1, 0, scopedFetch('/api/reportes/flujo-caja-proyectado?meses=3'));
      }

      const results = await Promise.all(requests);
      let idx = 0;
      const flujoRes = results[idx++];
      if (hasProfesional) {
        const proyRes = results[idx++];
        if (proyRes.ok) setProyeccion(await proyRes.json());
        else setProyeccion(null);
      } else {
        setProyeccion(null);
      }
      const calRes = results[idx++];
      const invRes = results[idx];

      if (flujoRes.ok) setFlujo(await flujoRes.json());
      else setFlujo(null);

      if (calRes.ok) setCalendario(await calRes.json());
      else setCalendario(null);

      if (invRes.ok) {
        const list = await invRes.json();
        const pendientes = (Array.isArray(list) ? list : [])
          .filter(isPendiente)
          .sort((a, b) => daysOverdue(b) - daysOverdue(a));
        setImpagasTotal(pendientes.length);
        setImpagas(pendientes.slice(0, 10));
      } else {
        setImpagas([]);
        setImpagasTotal(0);
      }
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'No se pudo cargar la vista contador.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, scopedFetch, hasProfesional, isViewerPortal, activeCompanyId]);

  const loadNotes = useCallback(async () => {
    if (!isViewerPortal || !activeCompanyId) {
      setClientNotes([]);
      return;
    }
    try {
      const res = await scopedFetch('/api/contador/notas');
      if (!res.ok) return;
      const json = await res.json();
      setClientNotes(Array.isArray(json.notes) ? json.notes.slice(-3) : []);
    } catch {
      setClientNotes([]);
    }
  }, [isViewerPortal, activeCompanyId, scopedFetch]);

  useEffect(() => {
    if (!loadingClients) loadNotes();
  }, [loadNotes, loadingClients]);

  useEffect(() => {
    if (!loadingClients) load();
  }, [load, loadingClients]);

  const descargarPack = async () => {
    setExporting(true);
    try {
      const { desde, hasta } = monthRange();
      await downloadBinaryExport(
        scopedFetch,
        `/api/export/contador-pack?desde=${desde}&hasta=${hasta}`,
        'pack_contador.zip'
      );
    } catch (err) {
      alert(err.message || 'Error al descargar');
    } finally {
      setExporting(false);
    }
  };

  const proximosCalendario = useMemo(() => {
    const eventos = calendario?.eventos || calendario?.proximos || [];
    return eventos.filter((e) => !e.vencido).slice(0, 6);
  }, [calendario]);

  const empresaLabel = useMemo(
    () => activeClient?.companyName || user?.company || user?.name || 'Empresa',
    [activeClient, user]
  );

  const canDownloadPack = hasProfesional || isViewerPortal;

  const totalPendienteArs = useMemo(
    () => impagas.reduce((sum, f) => {
      const saldo = saldoPendiente(f);
      const moneda = String(f.moneda || 'ARS').toUpperCase();
      if (moneda === 'ARS') return sum + saldo;
      if (f.montoArs != null && f.total) return sum + (saldo / Number(f.total)) * Number(f.montoArs);
      return sum + saldo;
    }, 0),
    [impagas]
  );

  const flujoPositivo = (flujo?.flujoCaja ?? 0) >= 0;
  const green = modoOscuro ? 'text-green-300' : 'text-green-700';
  const red = modoOscuro ? 'text-red-300' : 'text-red-700';
  const amber = modoOscuro ? 'text-amber-300' : 'text-amber-700';

  return (
    <div className={modoOscuro ? 'min-h-[calc(100dvh-4rem)] bg-black text-white' : 'min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-5 pb-24 lg:pb-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-200 text-gray-600'
              }`}>
                <FiEye className="w-3 h-3" />
                Vista contador
              </span>
              {readOnly && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  modoOscuro ? 'bg-sky-900/40 text-sky-200' : 'bg-sky-50 text-sky-800'
                }`}>
                  Solo lectura
                </span>
              )}
              {calendario?.regimen && (
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'
                }`}>
                  {REGIMEN_LABELS[calendario.regimen] || calendario.regimen}
                </span>
              )}
            </div>
            <h1 className={`text-2xl sm:text-3xl font-semibold ${textPri}`}>{empresaLabel}</h1>
            <p className={`text-sm mt-1 ${textMuted}`}>
              Resumen de {monthLabel()} para tu contador: flujo, proyección, vencimientos e impagas.
              {lastUpdated && (
                <span className="ml-1">
                  · Actualizado {lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className={`inline-flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold disabled:opacity-50 shrink-0 ${btnOut}`}
          >
            <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </header>

        {isViewerPortal && (
          <ContadorClientPicker
            clients={clients}
            activeCompanyId={activeCompanyId}
            onChange={setActiveCompanyId}
            modoOscuro={modoOscuro}
            multiClient={multiClient}
          />
        )}

        {isViewerPortal && activeCompanyId && (
          <section className={`rounded-2xl border p-4 space-y-3 ${box}`}>
            <div>
              <h2 className={`text-sm font-semibold ${textPri}`}>Nota para el cliente</h2>
              <p className={`text-[11px] mt-0.5 ${textMuted}`}>
                La ve en Inicio. Mensaje operativo; no reemplaza un cierre fiscal.
              </p>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              maxLength={400}
              rows={3}
              placeholder="Ej. Recibí el pack de septiembre. Falta el extracto de MP."
              className={`w-full rounded-xl border px-3 py-2 text-sm ${
                modoOscuro
                  ? 'bg-gray-900 border-gray-700 text-white placeholder:text-gray-500'
                  : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
              }`}
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={noteSending || !noteText.trim()}
                onClick={async () => {
                  setNoteSending(true);
                  setNoteMessage('');
                  try {
                    const res = await scopedFetch('/api/contador/notas', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ text: noteText.trim() }),
                    });
                    const json = await res.json();
                    if (!res.ok) throw new Error(json.message || 'No se pudo enviar');
                    setNoteText('');
                    setClientNotes(Array.isArray(json.notes) ? json.notes.slice(-3) : []);
                    setNoteMessage(json.message);
                  } catch (err) {
                    setNoteMessage(err.message);
                  } finally {
                    setNoteSending(false);
                  }
                }}
                className="px-3 py-2 rounded-xl text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
              >
                {noteSending ? 'Enviando…' : 'Enviar al cliente'}
              </button>
              {noteMessage && <p className={`text-[11px] ${textMuted}`}>{noteMessage}</p>}
            </div>
            {clientNotes.length > 0 && (
              <ul className="space-y-1.5">
                {clientNotes.map((note) => (
                  <li key={note.id} className={`text-[11px] leading-relaxed ${textSec}`}>
                    {note.text}
                    {note.readAt ? ' · leída' : ''}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {!loading && (
          <div className="lg:hidden -mx-1 overflow-x-auto px-1">
            <div className="flex gap-1.5 min-w-max pb-1">
              {MOBILE_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => scrollToContadorSection(section.id)}
                  className={`shrink-0 px-3 py-2 rounded-full text-xs font-semibold border transition-colors ${
                    modoOscuro
                      ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {section.label}
                  {section.id === 'impagas' && impagasTotal > 0 ? ` (${impagasTotal})` : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="-mx-4 overflow-x-auto px-4 lg:mx-0 lg:overflow-visible lg:px-0">
          <div className="flex gap-2 min-w-max pb-1 lg:min-w-0 lg:flex-wrap">
            <Link
              to="/reportes?tab=flujo"
              className="inline-flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] shrink-0"
            >
              <FiBarChart2 className="w-4 h-4" />
              Reportes
            </Link>
            <Link
              to="/facturas?estado=impaga"
              className={`inline-flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold shrink-0 ${btnOut}`}
            >
              <FiFileText className="w-4 h-4" />
              Impagas
              {impagasTotal > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  modoOscuro ? 'bg-amber-900/50 text-amber-200' : 'bg-amber-100 text-amber-800'
                }`}>
                  {impagasTotal}
                </span>
              )}
            </Link>
            <Link
              to="/cuenta?tab=fiscal"
              className={`inline-flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold shrink-0 ${btnOut}`}
            >
              <FiCalendar className="w-4 h-4" />
              Fiscal
            </Link>
            {canDownloadPack && (
              <button
                type="button"
                onClick={descargarPack}
                disabled={exporting}
                className={`hidden lg:inline-flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold disabled:opacity-50 shrink-0 ${btnOut}`}
              >
                <FiDownload className="w-4 h-4" />
                {exporting ? 'Generando…' : 'Pack ZIP'}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className={`rounded-xl border px-4 py-3 flex items-start gap-2 text-sm ${
            modoOscuro ? 'border-red-900/50 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-800'
          }`}>
            <FiAlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <Skeleton box={box} modoOscuro={modoOscuro} />
        ) : (
          <>
            {flujo && (
              <section id="contador-flujo" className={`rounded-2xl border overflow-hidden scroll-mt-20 ${box}`}>
                <div className={`px-4 py-3 border-b flex items-center justify-between gap-2 ${divide}`}>
                  <h2 className={`text-sm font-semibold ${textPri}`}>Flujo del mes</h2>
                  <span className={`text-xs ${textMuted}`}>Cobrado − Egresos</span>
                </div>
                <div className={`grid grid-cols-2 lg:grid-cols-4 gap-px ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <KpiCard
                    label="Cobrado"
                    value={formatMoney(flujo.ventasCobradas, 'ARS')}
                    hint={`${flujo.cantidadVentas || 0} ventas`}
                    color={green}
                    modoOscuro={modoOscuro}
                  />
                  <KpiCard
                    label="Pendiente de cobro"
                    value={formatMoney(flujo.ventasImpagas, 'ARS')}
                    color={amber}
                    modoOscuro={modoOscuro}
                  />
                  <KpiCard
                    label="Egresos"
                    value={formatMoney(flujo.egresosTotal, 'ARS')}
                    hint={`${flujo.cantidadEgresos || 0} gastos`}
                    color={red}
                    modoOscuro={modoOscuro}
                  />
                  <KpiCard
                    label="Flujo neto"
                    value={formatMoney(flujo.flujoCaja, 'ARS')}
                    hint={flujoPositivo ? 'Superávit del mes' : 'Déficit del mes'}
                    color={flujoPositivo ? green : red}
                    modoOscuro={modoOscuro}
                    highlight
                  />
                </div>
              </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <section id="contador-proyeccion" className={`rounded-2xl border overflow-hidden scroll-mt-20 ${box}`}>
                <div className={`px-4 py-3 border-b flex items-center gap-2 ${divide}`}>
                  <FiTrendingUp className="w-4 h-4 text-[#1F80FF]" />
                  <h2 className={`text-sm font-semibold ${textPri}`}>Flujo proyectado · 3 meses</h2>
                </div>
                {!hasProfesional ? (
                  <div className={`px-4 py-8 text-center ${textMuted}`}>
                    <FiLock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Disponible en plan Profesional o superior.</p>
                    <Link to="/planes" className="text-sm font-semibold text-[#1F80FF] hover:underline mt-1 inline-block">
                      Ver planes
                    </Link>
                  </div>
                ) : (
                  <ProjectedCashflowPanel
                    proyeccion={proyeccion}
                    formatAmount={(n) => formatMoney(n, 'ARS')}
                    modoOscuro={modoOscuro}
                    divide={divide}
                    textSec={textSec}
                    textMuted={textMuted}
                    showMiniBar
                  />
                )}
              </section>

              <section id="contador-fiscal" className={`rounded-2xl border overflow-hidden scroll-mt-20 ${box}`}>
                <div className={`px-4 py-3 border-b flex items-center justify-between gap-2 ${divide}`}>
                  <div className="flex items-center gap-2">
                    <FiCalendar className="w-4 h-4 text-[#1F80FF]" />
                    <h2 className={`text-sm font-semibold ${textPri}`}>Agenda fiscal</h2>
                  </div>
                  <Link to="/cuenta?tab=fiscal" className="text-xs font-semibold text-[#1F80FF] hover:underline">
                    Configurar
                  </Link>
                </div>
                {!calendario?.regimen ? (
                  <div className={`px-4 py-8 text-center ${textMuted}`}>
                    <p className="text-sm">Todavía no declaraste tu régimen fiscal.</p>
                    <Link to="/cuenta?tab=fiscal" className="text-sm font-semibold text-[#1F80FF] hover:underline mt-1 inline-block">
                      Completar perfil fiscal
                    </Link>
                  </div>
                ) : proximosCalendario.length === 0 ? (
                  <p className={`text-sm px-4 py-8 text-center ${textMuted}`}>
                    No hay vencimientos próximos en el calendario.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                    {proximosCalendario.map((ev) => {
                      const urg = fiscalUrgency(ev);
                      return (
                        <li key={ev.id} className="px-4 py-3 flex gap-3">
                          <span className={`shrink-0 self-start px-2 py-0.5 rounded-md text-[10px] font-bold ${urgencyClasses(urg.tone, modoOscuro)}`}>
                            {urg.label}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium ${textPri}`}>{ev.titulo}</div>
                            {ev.descripcion && (
                              <div className={`text-xs mt-0.5 ${textMuted}`}>{ev.descripcion}</div>
                            )}
                            {ev.tipo === 'alerta' && (
                              <span className={`inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700'
                              }`}>
                                Requiere atención
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>

            <section id="contador-impagas" className={`rounded-2xl border overflow-hidden scroll-mt-20 ${box}`}>
              <div className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2 ${divide}`}>
                <div>
                  <h2 className={`text-sm font-semibold ${textPri}`}>Comprobantes impagos</h2>
                  {impagasTotal > 0 && (
                    <p className={`text-xs mt-0.5 ${textMuted}`}>
                      {impagasTotal} pendiente{impagasTotal !== 1 ? 's' : ''}
                      {totalPendienteArs > 0 && (
                        <> · saldo visible {formatMoney(totalPendienteArs, 'ARS')}</>
                      )}
                    </p>
                  )}
                </div>
                {impagasTotal > 10 && (
                  <Link to="/facturas?estado=impaga" className="text-xs font-semibold text-[#1F80FF] hover:underline">
                    Ver las {impagasTotal}
                  </Link>
                )}
              </div>
              {impagas.length === 0 ? (
                <div className={`px-4 py-10 text-center ${textMuted}`}>
                  <p className="text-sm">No hay saldos pendientes registrados.</p>
                  <p className="text-xs mt-1">¡Buen trabajo con la cobranza!</p>
                </div>
              ) : (
                <>
                  <div className={`md:hidden divide-y ${divide}`}>
                    {impagas.map((f) => (
                      <Link
                        key={f._id || f.id}
                        to="/facturas?estado=impaga"
                        className={`block px-4 py-3.5 ${modoOscuro ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}
                      >
                        <div className="flex justify-between gap-3">
                          <div className="min-w-0">
                            <div className={`text-sm font-semibold truncate ${textPri}`}>{f.razonSocial || '—'}</div>
                            <div className={`text-xs mt-0.5 ${textMuted}`}>N° {f.numero} · vto. {formatFecha(f.vencimiento || f.fecha)}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`text-base font-bold tabular-nums ${textPri}`}>
                              {formatMoney(saldoPendiente(f), f.moneda || 'ARS')}
                            </div>
                            {agingLabel(f) && (
                              <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${agingTone(f, modoOscuro)}`}>
                                {agingLabel(f)}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`text-[10px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                          <th className="text-left font-semibold py-2 px-4">Cliente</th>
                          <th className="text-left font-semibold py-2 px-3">N°</th>
                          <th className="text-left font-semibold py-2 px-3">Vencimiento</th>
                          <th className="text-center font-semibold py-2 px-3">Antigüedad</th>
                          <th className="text-right font-semibold py-2 px-4">Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impagas.map((f) => (
                          <tr key={f._id || f.id} className={`border-t ${divide}`}>
                            <td className={`py-2.5 px-4 text-sm font-medium ${textPri}`}>{f.razonSocial || '—'}</td>
                            <td className={`py-2.5 px-3 text-sm font-mono ${textSec}`}>#{f.numero}</td>
                            <td className={`py-2.5 px-3 text-sm ${textSec}`}>{formatFecha(f.vencimiento || f.fecha)}</td>
                            <td className="py-2.5 px-3 text-center">
                              {agingLabel(f) ? (
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${agingTone(f, modoOscuro)}`}>
                                  {agingLabel(f)}
                                </span>
                              ) : '—'}
                            </td>
                            <td className={`py-2.5 px-4 text-sm text-right font-semibold tabular-nums ${textPri}`}>
                              {formatMoney(saldoPendiente(f), f.moneda || 'ARS')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>

      {canDownloadPack && (
        <div className={`fixed bottom-0 inset-x-0 z-40 lg:hidden border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] ${
          modoOscuro ? 'border-gray-800 bg-gray-950/95 backdrop-blur' : 'border-gray-200 bg-white/95 backdrop-blur'
        }`}>
          <div className="max-w-6xl mx-auto grid grid-cols-2 gap-2">
            <Link
              to="/reportes?tab=flujo"
              className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border text-sm font-semibold ${btnOut}`}
            >
              <FiBarChart2 className="w-4 h-4" />
              Reportes
            </Link>
            <button
              type="button"
              onClick={descargarPack}
              disabled={exporting}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
            >
              <FiDownload className="w-4 h-4" />
              {exporting ? 'Generando…' : 'Pack ZIP'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
